import "@/polyfills/closest";
import "@/modules/modals";
import "@/services/google-login";
import events from "@/events";
import user from "@/utils/user";
import globals from "@/utils/globals";
import getLocale from "@/utils/locale";

// As Vue destroy the modals on close,
// we need to execute replace logic on modal open
// That's why we save them on trigger click,
// replace on modal open, delete saved values on modal close

let signupPath;
let loginPath;
let baseSignupHeadlineCopy;
let baseSignupSubheadlineCopy;
let signupHeadlineCopy;
let signupSubheadlineCopy;
let eventCategory;

import SignupForm from "@/component-library/signup-form/index";
import createComponent  from "@/utils/create-component";

const LONG_ONBOARDING_ROUTES = [
  "/lp/"
];

events.on("modal:auth-popup:step:signup:opened", () => {
  /* Replace headline and subheadline if needed */
  const signupHeadline = document.querySelector(".auth-popup-form__headline");
  const signupSubheadline = document.querySelector(".auth-popup-form__subheadline");
  // Save default copies
  if (!baseSignupHeadlineCopy) {
    baseSignupHeadlineCopy = signupHeadline.innerHTML;
  }
  if (!baseSignupSubheadlineCopy) {
    baseSignupSubheadlineCopy = signupSubheadline.innerHTML;
  }
  // Replace if different
  signupHeadline.innerHTML = signupHeadlineCopy || baseSignupHeadlineCopy;
  signupSubheadline.innerHTML = signupSubheadlineCopy || baseSignupSubheadlineCopy;

  // Replace signup action if needed
  const signupForm = document.querySelector(".js-popup-signup-form");
  if (signupForm && signupPath) {
    signupForm.setAttribute("action", signupPath);
  }

  if (signupForm && eventCategory) {
    const formIdField = signupForm.querySelector("input#signup-popup_signup_form_id");

    if (formIdField) {
      formIdField.value = eventCategory;
    }
  }

  createVuejsSignupForm();

  events.emit("facebook:init");
});

function createVuejsSignupForm() {
  const rootElement = document.getElementById("signup_form_vuejs");

  if (!rootElement) {
    return;
  }

  const bookToAdd = globals.bookSlug;

  const props = {};

  if (bookToAdd) {
    props.hiddenFields = [{ name: "signup[books_to_add][]", value: bookToAdd }];
  }

  if (signupPath) {
    const url =new URLSearchParams(signupPath.split("?")[1]).get("last_page_before_login");
    props.lastPageBeforeLogin = url;
  }

  createComponent({
    el: rootElement,
    component: SignupForm,
    props: props
  });
}

events.on("modal:auth-popup:step:login:opened", () => {
  // Adjust login action and apple sign-in path if needed - e.g. for last_page_before_login
  if (loginPath) {
    const loginForm = document.querySelector(".js-popup-login-form");
    if (loginForm && loginPath) {
      loginForm.setAttribute("action", loginPath);
    }

    const appleLoginButton = document.querySelector(".js-popup-login-apple-button");
    if (appleLoginButton) {
      const urlParams = loginPath.split("?")[1];
      if (urlParams) {
        appleLoginButton.setAttribute("href", appleLoginButton.getAttribute("href") + "?" + urlParams);
      }
    }
  }

  events.emit("facebook:init");
  events.emit("google:login");
});

events.on("modal:auth-popup:closed", () => {
  // Delete saved values on popup closed
  signupHeadlineCopy = null;
  signupSubheadlineCopy = null;
  signupPath = null;
  loginPath = null;
});

document.addEventListener("click", e => {
  // Global class to open signup on click
  const openSignupTarget = e.target.closest(".js-open-signup-popup");
  if (openSignupTarget && !user.isLoggedIn()) {
    e.preventDefault();

    setSignupCopies(openSignupTarget);

    onAuthTriggerAction("signup", openSignupTarget);
  }

  // Global class to open login on click
  const openLoginTarget = e.target.closest(".js-open-login-popup");
  if (openLoginTarget && !user.isLoggedIn()) {
    e.preventDefault();

    onAuthTriggerAction("login", openLoginTarget);
  }

  // Save signup action if needed
  const signupTarget = e.target.closest("[data-signup-path]");
  if (signupTarget) {
    signupPath = signupTarget.dataset.signupPath;
  }

  // Save login action if needed
  const loginTarget = e.target.closest("[data-login-path]");
  if (loginTarget) {
    loginPath = loginTarget.dataset.loginPath;
  }

  // Show / hide password
  const passwordTarget = e.target.closest(".js-password-toggle");
  if (passwordTarget) {
    const field = passwordTarget.closest(".form__field");
    if (field) {
      const input = field.querySelector("input[name='signup[password]']");
      if (input.getAttribute("type") === "password") {
        input.setAttribute("type", "text");
        passwordTarget.classList.add("password-toggle--shown");
      } else {
        input.setAttribute("type", "password");
        passwordTarget.classList.remove("password-toggle--shown");
      }
      input.focus();
    }
  }

  const ssoButtonTarget = e.target.closest(".js-sso-login-button");
  if (ssoButtonTarget) {
    e.preventDefault();
    // This navigation to the auth flow has to be done via POST to skip the /auth/b2b page
    // and directly go to the auth0 login page. Navigating to auth0 needs to go through a POST
    // request first to set a nonce and prevent CSRF attacks.
    // In plain rails we would do `link_to("/auth/b2b", method:post)` but this requires jquery and
    // jquery_ui on the page which we don't have for modern pages anymore.
    const form = document.createElement("form");
    form.style.visibility = "hidden";
    form.method = "POST";
    form.action = "/auth/b2b";
    const csrfToken = document.createElement("input");
    csrfToken.name = "authenticity_token";
    csrfToken.value = document.querySelector("meta[name=csrf-token]").getAttribute("content");
    form.appendChild(csrfToken);
    document.body.appendChild(form);
    form.submit();
  }
});

// Save auth copy if data attributes are set
// data-signup-headline / data-signup-subheadline
const setSignupCopies = (trigger) => {
  signupHeadlineCopy = trigger.dataset.signupHeadline;
  signupSubheadlineCopy = trigger.dataset.signupSubheadline;
};

function onAuthTriggerAction(type, target) {
  if (user.isLoggedIn() && target.classList.contains("js-redirect-on-auth-trigger")) {
    if (!target.href) {
      return;
    }
  } else {

    const isLongOnboardingRoute = LONG_ONBOARDING_ROUTES.some(route => window.location.href.includes(route));

    if (type === "signup" && isLongOnboardingRoute) {
      window.location.href = `/${getLocale()}/onboarding/welcome-wizard/start`;
      return;
    }

    eventCategory = target.dataset && target.dataset.eventCategory;

    if (globals.seeSignupDiscount) {
      events.emit(`abtest:modal:auth-popup:step:${type}:show`);
      return;
    }

    events.emit(`modal:auth-popup:step:${type}:show`);
  }
}
